import { Fragment, useMemo } from "react";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/solid";
import { useCurrency } from "./CurrencyContext";
import countryCurrencyMap from "./countryCurrencyMap";

const LOCAL_STORAGE_KEY = "ticketverz_currency";
const FORCE_CURRENCY_KEY = "ticketverz_force_currency";

const POPULAR_CURRENCY_CODES = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
];

const CURRENCY_LABELS = {
  AED: "United Arab Emirates Dirham",
  AUD: "Australian Dollar",
  BGN: "Bulgarian Lev",
  CAD: "Canadian Dollar",
  CHF: "Swiss Franc",
  CZK: "Czech Koruna",
  DKK: "Danish Krone",
  EUR: "Euro",
  GBP: "British Pound",
  HKD: "Hong Kong Dollar",
  HUF: "Hungarian Forint",
  JPY: "Japanese Yen",
  MXN: "Mexican Peso",
  NOK: "Norwegian Krone",
  NZD: "New Zealand Dollar",
  PLN: "Polish Złoty",
  RON: "Romanian Leu",
  SEK: "Swedish Krona",
  SGD: "Singapore Dollar",
  THB: "Thai Baht",
  USD: "US Dollar",
};


function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getUniqueCurrenciesFromMap(map) {
  const unique = new Map();

  Object.values(map).forEach((currency) => {
    if (!currency?.code || !currency?.symbol) return;

    if (!unique.has(currency.code)) {
      unique.set(currency.code, {
        code: currency.code,
        symbol: currency.symbol,
        label: CURRENCY_LABELS[currency.code] || currency.code,
      });
    }
  });

  return Array.from(unique.values()).sort((a, b) =>
    a.code.localeCompare(b.code),
  );
}

export default function CurrencySwitcher({
  className = "",
  align = "right",
  compact = false,
}) {
  const { currency, setCurrencyDetected } = useCurrency();

  const currencies = useMemo(
    () => getUniqueCurrenciesFromMap(countryCurrencyMap),
    [],
  );

  const popularCurrencies = useMemo(
    () =>
      POPULAR_CURRENCY_CODES.map((code) =>
        currencies.find((item) => item.code === code),
      ).filter(Boolean),
    [currencies],
  );

  const otherCurrencies = useMemo(
    () =>
      currencies.filter((item) => !POPULAR_CURRENCY_CODES.includes(item.code)),
    [currencies],
  );

  const currentCurrency = useMemo(() => {
    if (!currency?.code) {
      return currencies.find((item) => item.code === "EUR") || currencies[0];
    }

    return (
      currencies.find((item) => item.code === currency.code) || {
        code: currency.code,
        symbol: currency.symbol || "€",
        label: currency.code,
      }
    );
  }, [currency, currencies]);

  const handleSelectCurrency = (selected) => {
    const payload = {
      code: selected.code,
      symbol: selected.symbol,
    };

    setCurrencyDetected(payload);

    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
      localStorage.removeItem(FORCE_CURRENCY_KEY);
    } catch {}

    if (currentCurrency.code !== selected.code) {
      window.location.reload();
    }
  };

  const renderRowItem = (item) => {
    const isActive = currentCurrency.code === item.code;

    return (
      <MenuItem key={item.code}>
        {({ focus }) => (
          <button
            type="button"
            onClick={() => handleSelectCurrency(item)}
            className={classNames(
              "flex w-full items-center justify-between rounded-2xl px-3 py-3 text-left transition-all duration-150",
              focus ? "bg-slate-50" : "",
              isActive
                ? "bg-gradient-to-r from-indigo-50 to-purple-50 ring-1 ring-indigo-100"
                : "hover:bg-slate-50",
            )}
          >
            <div className="flex min-w-0 items-center gap-3">
              <div
                className={classNames(
                  "flex h-11 w-11 items-center justify-center rounded-2xl text-base font-bold shadow-sm",
                  isActive
                    ? "bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700"
                    : "bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700",
                )}
              >
                {item.symbol}
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {item.code}
                  </span>
                  <span className="text-sm text-gray-500">{item.symbol}</span>
                </div>
                <p className="truncate text-xs text-gray-500">{item.label}</p>
              </div>
            </div>

            {isActive ? (
              <CheckIcon
                className="h-5 w-5 shrink-0 text-indigo-600"
                aria-hidden="true"
              />
            ) : (
              <span className="h-5 w-5 shrink-0" />
            )}
          </button>
        )}
      </MenuItem>
    );
  };

  return (
    <Menu as="div" className={classNames("relative shrink-0", className)}>
      <MenuButton
        className={classNames(
          "group inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white shadow-md backdrop-blur-md transition-all duration-200 hover:border-white/35 hover:bg-white/20 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-white/60",
          compact ? "px-2.5 py-1.5 text-xs" : "px-3.5 py-2 text-sm",
        )}
        aria-label="Select currency"
      >
        <span className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-sm leading-none">
            {currentCurrency.symbol}
          </span>
          <span className="font-semibold tracking-wide">
            {currentCurrency.code}
          </span>
        </span>

        <ChevronDownIcon
          className={classNames(
            "ml-1.5 text-white/80 transition-transform duration-200 group-data-[headlessui-state=open]:rotate-180",
            compact ? "h-3.5 w-3.5" : "h-4 w-4",
          )}
          aria-hidden="true"
        />
      </MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="opacity-0 scale-95 -translate-y-1"
        enterTo="opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 scale-100 translate-y-0"
        leaveTo="opacity-0 scale-95 -translate-y-1"
      >
        <MenuItems
          anchor={align === "left" ? "bottom start" : "bottom end"}
          className="z-[80] mt-3 w-[320px] max-w-[92vw] overflow-hidden rounded-3xl border border-white/20 bg-white/95 shadow-2xl ring-1 ring-black/5 backdrop-blur-2xl focus:outline-none"
        >
          <div className="border-b border-slate-100 px-5 pb-4 pt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              Currency
            </p>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Prices shown in {currentCurrency.code}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Change your browsing currency anytime.
                </p>
              </div>

              <div className="flex h-11 min-w-[56px] items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 px-3 text-base font-bold text-indigo-700">
                {currentCurrency.symbol}
              </div>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto p-3">
            {popularCurrencies.length > 0 && (
              <div className="mb-4">
                <div className="px-2 pb-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    Popular Currencies
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {popularCurrencies.map((item) => {
                    const isActive = currentCurrency.code === item.code;

                    return (
                      <button
                        key={item.code}
                        type="button"
                        onClick={() => handleSelectCurrency(item)}
                        className={classNames(
                          "inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-semibold transition-all duration-150",
                          isActive
                            ? "border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 shadow-sm"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
                        )}
                      >
                        <span>{item.symbol}</span>
                        <span>{item.code}</span>
                        {isActive && (
                          <CheckIcon className="h-4 w-4 text-indigo-600" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="my-3 border-t border-slate-100" />

            <div>
              <div className="px-2 pb-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  All supported currencies
                </p>
              </div>

              <div className="space-y-1">
                {otherCurrencies.map(renderRowItem)}
              </div>
            </div>
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
}
